import { z } from "zod";

const phoneRegex = /^(\+91)?[6-9][0-9]{9}$/;
const pincodeRegex = /^[0-9]{6}$/;
const gstRegex = /^[0-9A-Z]{15}$/;
const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const optionalString = (max = 200) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === "" ? undefined : v));

export const documentMetaSchema = z.object({
  docType: z.string().min(1),
  label: z.string().optional(),
  docNumber: z.string().optional(),
  issuedAt: z.string().optional(),
  expiryDate: z.string().optional(),
});

export const pendingDocumentSchema = z.object({
  file: z.any(),
  meta: documentMetaSchema,
});

export const pendingPhotoSchema = z.object({
  file: z.any(),
  preview: z.string(),
  isCover: z.boolean(),
});

export const bankAccountSchema = z
  .object({
    accountName: z.string().optional(),
    accountNumber: z.string().optional(),
    ifsc: z.string().optional(),
    bankName: z.string().optional(),
    branch: z.string().optional(),
  })
  .optional();

export const wizardSchema = z.object({
  ownerId: z.string().uuid({ message: "Select an owner" }),

  businessName: z.string().trim().min(3, "At least 3 characters").max(150),
  nameEn: z.string().trim().min(3, "At least 3 characters").max(150),
  nameMl: z.string().trim().min(3, "At least 3 characters").max(150),
  descriptionEn: optionalString(5000),
  descriptionMl: optionalString(5000),
  logoUrl: optionalString(2000),

  address: z.string().trim().min(10, "At least 10 characters").max(500),
  city: z.string().trim().min(2).max(60),
  district: optionalString(60),
  state: optionalString(60),
  pincode: z.string().regex(pincodeRegex, "Must be 6 digits"),
  lat: z.number({ error: "Pick a location on the map" }).min(-90).max(90),
  lng: z.number({ error: "Pick a location on the map" }).min(-180).max(180),

  phone: z.string().regex(phoneRegex, "Format: +91 followed by 10 digits"),
  email: z.union([z.literal(""), z.email("Invalid email")]).optional(),
  whatsapp: z.string().trim().max(30).optional(),

  instagramUrl: z
    .union([z.literal(""), z.string().url("Must be a valid URL").max(300)])
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  facebookUrl: z
    .union([z.literal(""), z.string().url("Must be a valid URL").max(300)])
    .optional()
    .transform((v) => (v === "" ? undefined : v)),
  youtubeUrl: z
    .union([z.literal(""), z.string().url("Must be a valid URL").max(300)])
    .optional()
    .transform((v) => (v === "" ? undefined : v)),

  careTypes: z
    .array(z.enum(["prenatal", "postnatal"]))
    .min(1, "Pick at least one care type"),
  amenityIds: z.array(z.string()).min(1, "Select at least one amenity"),

  gstNumber: z
    .union([
      z.literal(""),
      z.string().regex(gstRegex, "Must be 15 chars, A–Z/0–9"),
    ])
    .optional(),
  panNumber: z
    .union([z.literal(""), z.string().regex(panRegex, "Invalid PAN format")])
    .optional(),
  tradeLicenseNumber: optionalString(50),
  fssaiNumber: z
    .union([z.literal(""), z.string().length(14, "Must be 14 digits")])
    .optional(),
  shopEstablishmentNumber: optionalString(50),

  isMedicalProfessional: z.boolean().optional(),
  medicalRegistrationNo: optionalString(50),
  medicalCouncil: optionalString(100),
  medicalQualifications: optionalString(2000),

  bankAccount: bankAccountSchema,
  upiId: optionalString(100),

  pendingDocuments: z.array(pendingDocumentSchema).optional(),
  pendingPhotos: z.array(pendingPhotoSchema).optional(),
});

export type WizardValues = z.input<typeof wizardSchema>;
export type WizardOutput = z.output<typeof wizardSchema>;

export const STEP_FIELDS = [
  ["ownerId"],
  ["businessName", "nameEn", "nameMl", "descriptionEn", "descriptionMl"],
  [
    "address",
    "city",
    "district",
    "pincode",
    "lat",
    "lng",
    "phone",
    "email",
    "whatsapp",
    "instagramUrl",
    "facebookUrl",
    "youtubeUrl",
  ],
  ["careTypes", "amenityIds"],
  ["pendingPhotos"],
  [
    "gstNumber",
    "panNumber",
    "tradeLicenseNumber",
    "fssaiNumber",
    "shopEstablishmentNumber",
    "isMedicalProfessional",
    "medicalRegistrationNo",
    "medicalCouncil",
    "medicalQualifications",
  ],
] as const satisfies ReadonlyArray<ReadonlyArray<keyof WizardValues>>;

export const STEP_LABELS = [
  "Owner",
  "Basics",
  "Location",
  "Facility",
  "Images",
  "Compliance",
] as const;

export const defaultValues: Partial<WizardValues> = {
  careTypes: [],
  amenityIds: [],
  pendingPhotos: [],
  pendingDocuments: [],
  isMedicalProfessional: false,
};
